import { useFormContext } from 'react-hook-form';
import { FormField } from 'ui/formField';
import {
  BasicSelectField,
  InputField,
  RadioListField,
  TimeZonePickerField,
} from 'lib/iterable-hook-form/FormField';
import { shardCountOptions } from 'modules/projects/components/CreateProjectModal.constants';
import { useUniqueIdentifierOptions } from 'modules/projects/hooks/useUniqueIdentifierOptions';
import { ORG_FORM_PROJECT_FIELDS } from '../featureFlags';
import {
  SHARD_COUNT,
  PROJECT_NAME,
  PROJECT_TIME_ZONE,
  BOOTSTRAP_ACCOUNT,
  UNIQUE_IDENTIFIER,
} from '../constants';
import {
  CheckboxGrid,
  LightBody2,
  LightBody2WithPointer,
  LinkSmallText,
  CheckboxFieldWithMarginTop,
} from './FormSection.styled';
import { FormSection } from './FormSection';
import type { NewOrganizationFormSchemaType } from '../contexts/NewOrganizationFormProvider';

export const TEXT = {
  HEADING: 'Project',
  DESCRIPTION: 'Create the first project that will be added to this organization.',
  PROJECT_NAME_LABEL: 'Project name',
  TIME_ZONE_LABEL: 'Timezone',
  UNIQUE_IDENTIFIER_LABEL: 'Unique identifier',
  SHARD_COUNT_LABEL: 'Estimated combined users and custom events usage',
  BOOTSTRAP_ACCOUNT_LABEL: 'Include prebuilt template and campaign data (optional)',
};

export const ProjectFormSection = () => {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<NewOrganizationFormSchemaType>();
  const uniqueIdentifierOptions = useUniqueIdentifierOptions();

  const bootstrapAccountChecked = watch(BOOTSTRAP_ACCOUNT);
  const toggleBootstrapAccount = () => {
    setValue(BOOTSTRAP_ACCOUNT, !bootstrapAccountChecked);
  };

  const projectNameError = errors.projectName?.message;
  const timezoneError = errors.projectTimeZone?.message;
  const uniqueIdentifierError = errors.uniqueIdentifier?.message;
  const shardCountError = errors.shardCount?.message;
  const bootstrapAccountError = errors.bootstrapAccount?.message;

  return (
    <FormSection id="project" headingText={TEXT.HEADING} descriptionText={TEXT.DESCRIPTION}>
      {ORG_FORM_PROJECT_FIELDS && (
        <>
          <FormField
            labelText={TEXT.PROJECT_NAME_LABEL}
            errorText={projectNameError}
            hasError={!!projectNameError}
            inputId={PROJECT_NAME}
            isHorizontalLayout
          >
            <InputField id={PROJECT_NAME} name={PROJECT_NAME} />
          </FormField>

          <FormField
            labelText={TEXT.TIME_ZONE_LABEL}
            errorText={timezoneError}
            hasError={!!timezoneError}
            inputId={PROJECT_TIME_ZONE}
            isHorizontalLayout
          >
            <TimeZonePickerField name={PROJECT_TIME_ZONE} />
          </FormField>
        </>
      )}

      <FormField
        labelText={TEXT.UNIQUE_IDENTIFIER_LABEL}
        errorText={uniqueIdentifierError}
        hasError={!!uniqueIdentifierError}
        inputId={UNIQUE_IDENTIFIER}
        isHorizontalLayout
      >
        <LightBody2>
          Select the field(s) you’ll use to identify users. This impacts some features and APIs.
          Note, this can’t be changed later for this project, but you can select a different unique
          identifier for future projects. Learn more in our&nbsp;
          <LinkSmallText
            href="https://support.iterable.com/hc/articles/9216719179796"
            target="_blank"
            rel="noopener noreferrer"
            hasOpenNewTabIcon
          >
            knowledge center
          </LinkSmallText>
          .
        </LightBody2>
        <RadioListField name={UNIQUE_IDENTIFIER} options={uniqueIdentifierOptions} />
      </FormField>

      <FormField
        labelText={TEXT.SHARD_COUNT_LABEL}
        errorText={shardCountError}
        hasError={!!shardCountError}
        inputId={SHARD_COUNT}
        isHorizontalLayout
      >
        <LightBody2>
          We’ll allocate database resources based on this estimated project size.
        </LightBody2>
        <BasicSelectField
          automaticallyAvoidCollisions
          id="shard-count-select"
          options={shardCountOptions}
          name={SHARD_COUNT}
          defaultValue={shardCountOptions[0].value}
        />
      </FormField>
      <FormField
        labelText={TEXT.BOOTSTRAP_ACCOUNT_LABEL}
        errorText={bootstrapAccountError}
        hasError={!!bootstrapAccountError}
        inputId={BOOTSTRAP_ACCOUNT}
        isHorizontalLayout
      >
        <CheckboxGrid>
          <CheckboxFieldWithMarginTop id="boostrap-account" name={BOOTSTRAP_ACCOUNT} />
          <div onClick={toggleBootstrapAccount}>
            <LightBody2WithPointer>
              If you’re creating a demo project, this option allow you to start with pre-loaded
              template and campaign data.
            </LightBody2WithPointer>
          </div>
        </CheckboxGrid>
      </FormField>
    </FormSection>
  );
};
